import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Interval } from '@/app/shared/constants/interval';
import { QueryKeys } from '@/app/shared/constants/queryKeys';
import { RANGE_BY_PERIOD, VAULT_TRANCHE } from '@/app/shared/constants/vaults';
import { mapPerformanceToChart } from '@/app/shared/lib/vaultPerformance';
import { getVaultPerformance } from '@/app/shared/services/api';
import { PerformanceChartData, PerformanceChartPeriod } from '@/app/shared/ui/PerformanceChart/types';

export type { VaultChartPoint } from '@/app/shared/lib/vaultPerformance';
export type { VaultChartSeriesKey } from '@/app/shared/constants/vaults';

export const useVaultPerformance = ({
  vaultId,
  period,
  metric,
}: {
  vaultId?: string;
  period: PerformanceChartPeriod;
  metric: PerformanceChartData;
}) => {
  const range = RANGE_BY_PERIOD[period];

  const { data, isLoading, error } = useQuery({
    queryKey: [QueryKeys.VAULT_PERFORMANCE, vaultId, range],
    queryFn: () => getVaultPerformance({ id: vaultId, range }),
    enabled: !!vaultId,
    retry: false,
    staleTime: Interval.ONE_MIN,
    refetchInterval: (query) => {
      const tranches = query.state.data?.tranches;
      return tranches && tranches.length > 0 ? Interval.ONE_MIN : false;
    },
  });

  const { points, series } = useMemo(
    () => mapPerformanceToChart(data?.tranches ?? [], metric),
    [data?.tranches, metric],
  );

  return {
    data,
    points,
    series,
    hasSenior: series.includes(VAULT_TRANCHE.SENIOR),
    hasJunior: series.includes(VAULT_TRANCHE.JUNIOR),
    isLoading,
    error,
  };
};
